// Native local-notification helpers. All no-op on web (guarded by
// Capacitor.isNativePlatform), so callers don't need their own guards.
import { Capacitor } from '@capacitor/core';

// Fixed IDs so we can cancel specific scheduled notifications later.
export const NOTIF_IDS = {
  BENDER_CHECKIN: [9001, 9002, 9003], // staggered "still on a bender?" nudges
  CHAT_BASE: 9100,                    // chat notifs use 9100 + rolling offset
};

let _lnPromise = null;
let _chatCounter = 0;

// Returns { LN } (or null on web). The plugin proxy is wrapped in a plain object
// so it is NEVER a promise's resolution value: resolving a promise WITH the
// Capacitor proxy makes JS call proxy.then(), which the bridge rejects as
// "LocalNotifications.then() is not implemented on ios".
function loadLN() {
  if (typeof window === 'undefined' || !Capacitor.isNativePlatform?.()) return null;
  if (!_lnPromise) {
    _lnPromise = import('@capacitor/local-notifications').then((m) => ({ LN: m.LocalNotifications }));
  }
  return _lnPromise;
}

/** Ask for notification permission if not already granted. Returns true if granted. */
export async function ensureNotifPermission() {
  const wrap = await loadLN();
  if (!wrap) return false;
  const LN = wrap.LN;
  try {
    const perm = await LN.checkPermissions();
    if (perm.display === 'granted') return true;
    const req = await LN.requestPermissions();
    return req.display === 'granted';
  } catch (e) {
    console.warn('Notification permission check failed:', e);
    return false;
  }
}

/** Show a notification immediately (best-effort; useful for chat pings). */
export async function fireNotification({ title, body, id }) {
  const wrap = await loadLN();
  if (!wrap) return;
  const LN = wrap.LN;
  try {
    await LN.schedule({
      notifications: [{
        id: id ?? (NOTIF_IDS.CHAT_BASE + (_chatCounter++ % 50)),
        title,
        body,
        schedule: { at: new Date(Date.now() + 200) },
      }],
    });
  } catch (e) {
    console.warn('fireNotification failed:', e);
  }
}

/**
 * Schedule the staggered "still on the bender?" check-ins relative to a session
 * start. iOS delivers these even when the app is suspended, so they work with
 * the screen off. Offsets are in hours from `startMs`; past times are skipped.
 */
// One nudge at 4h. Three a night (4/8/11h) was too many — the 12h auto-end
// already closes a forgotten session, so a single reminder is enough.
export async function scheduleBenderCheckins(startMs, offsetsHours = [4]) {
  const wrap = await loadLN();
  if (!wrap) return;
  const LN = wrap.LN;
  const now = Date.now();
  const notifications = offsetsHours
    .map((h, i) => ({ id: NOTIF_IDS.BENDER_CHECKIN[i], at: startMs + h * 60 * 60 * 1000 }))
    .filter((n) => n.id != null && n.at > now + 5000) // only future ones
    .map((n) => ({
      id: n.id,
      title: 'Still on a bender? 🍻',
      body: 'Tap to keep the night going — or wrap it up and see your recap.',
      schedule: { at: new Date(n.at) },
    }));
  if (notifications.length === 0) return;
  try {
    await LN.schedule({ notifications });
  } catch (e) {
    console.warn('scheduleBenderCheckins failed:', e);
  }
}

/** Cancel the scheduled bender check-ins (e.g. when the session ends). */
export async function cancelBenderCheckins() {
  const wrap = await loadLN();
  if (!wrap) return;
  const LN = wrap.LN;
  try {
    await LN.cancel({ notifications: NOTIF_IDS.BENDER_CHECKIN.map((id) => ({ id })) });
  } catch (e) {
    console.warn('cancelBenderCheckins failed:', e);
  }
}
